import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { CreateEmailAccountDto, EmailAccountDto, EmailMessageListItemDto, EmailSpamListItemDto, IPagedCollection, IPagingData, UpdateEmailAccountDto } from '@sr/dto';
import { APP_BASE_URL } from 'shared/api/api';
import { CollectionParams, setAuthHeader } from 'shared/api/rtk-query';
import { pagingDataToParams } from 'utils/remote-paged-collection.hook';

export const emailParserApi = createApi({
  reducerPath: 'email-parser',
  tagTypes: ['email-parser'],
  baseQuery: fetchBaseQuery({
    baseUrl: APP_BASE_URL,
    prepareHeaders: (headers, api) => {
      setAuthHeader(headers);
      return headers;
    }
  }),
  endpoints: (builder) => ({
    getEmailParserAccouns: builder.query<IPagedCollection<EmailAccountDto>, CollectionParams>({
      query: ({ paging, filter }: CollectionParams) => ({
        url: `email-parser/?${filter}`,
        params: paging && pagingDataToParams(paging)
      }),
      providesTags: (result, error, query) =>
        ([{ type: 'email-parser', id: 'LIST' }])
    }),
    getEmailAccount: builder.query<EmailAccountDto, number>({
      query: (emailAccountId) => ({
        url: `email-parser/datail/${emailAccountId}`,
        method: 'GET'
      }),
      providesTags: [{ type: 'email-parser', id: 'LIST' }] 
    }),
    createEmailAccountType: builder.mutation<EmailAccountDto, CreateEmailAccountDto>({
      query: (dto ) => ({
        url: 'email-parser/create',
        body: dto,
        method: 'POST'
      }),
      invalidatesTags: (result, error, query) =>
        ([{ type: 'email-parser', id: 'LIST' }])
    }),
    updateEmailAccount: builder.mutation<EmailAccountDto, UpdateEmailAccountDto>({
      query: ({ ...dto }) => ({
        url: 'email-parser/update',
        body: dto,
        method: 'PUT'
      }),
      invalidatesTags: (result, error, query) => [{ type: 'email-parser', id: 'LIST' }]
    }),
    collectEmailsForDays: builder.mutation<unknown, { accountId: number; days: number }>({
      query: ({ accountId, days }) => ({
        url: `email-parser/${accountId}/collect-for-days`,
        params: { days },
        method: 'POST'
      }),
      invalidatesTags: (result, error, query) => [{ type: 'email-parser', id: `LOG-${query.accountId}` }]
    }),
    getParseLog: builder.query({
      query: (accountId: number) => ({
        url: `email-parser/${accountId}/parse-log`,
        method: 'GET'
      }),
      providesTags: (result, error, accountId) => [{ type: 'email-parser', id: `LOG-${accountId}` }]
    }),
    getEmailMessages: builder.query<IPagedCollection<EmailMessageListItemDto>, { accountId: number; paging: IPagingData }>({
      query: ({ accountId, paging }) => ({
        url: `email-parser/${accountId}/messages`,
        params: pagingDataToParams(paging),
        method: 'GET'
      }),
      providesTags: (result, error, { accountId }) => [{ type: 'email-parser', id: `MESSAGES-${accountId}` }]
    }),
    getSpam: builder.query<IPagedCollection<EmailSpamListItemDto>, { paging: IPagingData; email?: string }>({
      query: ({ paging, email }) => ({
        url: 'email-spam',
        params: { ...pagingDataToParams(paging), ...(email && { email }) },
        method: 'GET'
      }),
      providesTags: (result, error, { email }) => [
        { type: 'email-parser', id: 'SPAM' },
        { type: 'email-parser', id: `SPAM-${email || 'ALL'}` }
      ]
    }),
    deleteSpam: builder.mutation<void, number>({
      query: (id) => ({
        url: `email-spam/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: (result, error, id) => [{ type: 'email-parser', id: 'SPAM-ALL' }]
    })
  })
});
